import { request } from '@/api/apiUtils';
import { createEntityQuery } from '@/data/createEntityQuery';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import type {
    EpisodePresentationTitle,
    IEpisode,
    IEpisodePresentation,
    IEpisodePresentationDuration,
    IEpisodePresentations,
    IEpisodeSearchPresentation,
    ResponseType,
} from '@/types';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const EPISODES_ROOT = ['episodes'] as const;
/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const EPISODES_SEARCH_ROOT = ['episodesSearch'] as const;
/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const EPISODE_PRESENTATIONS_ROOT = ['episodePresentations'] as const;
/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const EPISODE_PRESENTATIONS_TITLE_ROOT = ['episodePresentationsTitle'] as const;

interface EpisodesListParams {
    projectId: number;
    version?: string;
    episodeId?: number;
}

interface EpisodesSearchParams {
    projectId: number;
    version: string;
    feature?: number;
    tags?: string;
}

interface EpisodesPresentationsParams {
    projectId: number;
    version?: string;
}

interface EpisodesPresentationsListParams {
    projectId: number;
    version: string;
    tags?: string;
}

interface EpisodesPresentationDetailParams {
    projectId: number;
    episodeId: number;
    version: string;
    tags?: string;
}

interface EpisodesPresentationsTitleParams {
    projectId: number;
    episodeId: number;
    version?: string;
}

interface EpisodesPresentationsCharacterParams {
    projectId: number;
    characterEntityTagId: number;
}

const fetchers = {
    list: {
        key: ({ projectId, version, episodeId }: EpisodesListParams) =>
            [...EPISODES_ROOT, 'list', { projectId, version, episodeId }] as const,
        fetch: async ({ projectId, version, episodeId }: EpisodesListParams): Promise<IEpisode[]> => {
            const params = new URLSearchParams();
            if (version) params.set('version', version);
            const qs = params.toString();
            const url = `projects/${projectId}/episodes/${episodeId ?? ''}${qs ? `?${qs}` : ''}`;
            const response: ResponseType<IEpisode[]> = await request(url);
            return response.data;
        },
    },
    search: {
        key: (args: EpisodesSearchParams) => [...EPISODES_SEARCH_ROOT, 'list', args] as const,
        fetch: async ({ projectId, version, feature, tags }: EpisodesSearchParams): Promise<IEpisodeSearchPresentation[]> => {
            const viewParam = 'view[]=scenes';
            const tagsParam = tags ? `&${tags}` : '';
            const versionParam = version ? `&version=${version}` : '';
            const featureParam = feature ? `&tags[]=${feature}` : '';
            const url = `projects/${projectId}/presentations/episodes-search?${viewParam}${tagsParam}${versionParam}${featureParam}`;
            const response: ResponseType<IEpisodeSearchPresentation[]> = await request(url);
            return response.data;
        },
    },
    presentations: {
        key: ({ projectId, version }: EpisodesPresentationsParams) => [...EPISODES_ROOT, 'presentations', { projectId, version }] as const,
        fetch: async ({ projectId, version }: EpisodesPresentationsParams): Promise<IEpisodePresentationDuration[]> => {
            const params = new URLSearchParams();
            if (version) params.append('version', version);
            const endpoint = `projects/${projectId}/presentations/editor/episodes${params.toString() ? `?${params}` : ''}`;
            const data: { data: IEpisodePresentationDuration[] } = await request(endpoint);
            return data.data;
        },
    },
    presentationsList: {
        key: (args: EpisodesPresentationsListParams) => [...EPISODE_PRESENTATIONS_ROOT, 'list', args] as const,
        fetch: async ({ projectId, version, tags }: EpisodesPresentationsListParams): Promise<IEpisodePresentations[]> => {
            const viewParams = 'view[]=episodes&view[]=sequences&view[]=scenes';
            const tagsParam = tags ? `&${tags}` : '';
            const versionParam = version ? `&version=${version}` : '';
            const url = `projects/${projectId}/presentations/episodes?${viewParams}${tagsParam}${versionParam}`;
            const response: ResponseType<IEpisodePresentations[]> = await request(url);
            return response.data;
        },
    },
    presentationDetail: {
        key: (args: EpisodesPresentationDetailParams) => [...EPISODE_PRESENTATIONS_ROOT, 'detail', args] as const,
        fetch: async ({ projectId, episodeId, version, tags }: EpisodesPresentationDetailParams): Promise<IEpisodePresentation> => {
            const tagsParam = tags ? `?${tags}` : '';
            const versionParam = version ? `${tagsParam ? '&' : '?'}version=${version}` : '';
            const url = `projects/${projectId}/presentations/episodes/${episodeId}${tagsParam}${versionParam}`;
            const response: ResponseType<IEpisodePresentation> = await request(url);
            return response.data;
        },
    },
    presentationsTitle: {
        key: (args: EpisodesPresentationsTitleParams) => [...EPISODE_PRESENTATIONS_TITLE_ROOT, args] as const,
        fetch: async ({ projectId, episodeId, version }: EpisodesPresentationsTitleParams): Promise<EpisodePresentationTitle> => {
            const params = new URLSearchParams();
            if (version) params.set('version', version);
            const qs = params.toString();
            const response: ResponseType<EpisodePresentationTitle> = await request(
                `projects/${projectId}/presentations/episodes/${episodeId}/title${qs ? `?${qs}` : ''}`,
            );
            return response.data;
        },
    },
    presentationsCharacter: {
        key: (args: EpisodesPresentationsCharacterParams) => [...EPISODE_PRESENTATIONS_ROOT, 'character', args] as const,
        fetch: async ({ projectId, characterEntityTagId }: EpisodesPresentationsCharacterParams): Promise<IEpisode[]> => {
            const url = `projects/${projectId}/presentations/episodes-search?view[]=scenes&tags[]=${characterEntityTagId}`;
            const response: ResponseType<IEpisode[]> = await request(url);
            return response.data;
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const episodesEntity: EntityDefinition<typeof fetchers> = {
    name: 'episodes',
    roots: [EPISODES_ROOT, EPISODES_SEARCH_ROOT, EPISODE_PRESENTATIONS_ROOT, EPISODE_PRESENTATIONS_TITLE_ROOT],
    fetchers,
};

export const useEpisodesList = createEntityQuery(episodesEntity, 'list');
export const useEpisodesSearch = createEntityQuery(episodesEntity, 'search');
export const useEpisodesPresentations = createEntityQuery(episodesEntity, 'presentations');
export const useEpisodesPresentationsList = createEntityQuery(episodesEntity, 'presentationsList');
export const useEpisodesPresentationDetail = createEntityQuery(episodesEntity, 'presentationDetail');
export const useEpisodesPresentationsTitle = createEntityQuery(episodesEntity, 'presentationsTitle');
export const useEpisodesPresentationsCharacter = createEntityQuery(episodesEntity, 'presentationsCharacter');

export type {
    EpisodesListParams,
    EpisodesPresentationDetailParams,
    EpisodesPresentationsCharacterParams,
    EpisodesPresentationsListParams,
    EpisodesPresentationsParams,
    EpisodesPresentationsTitleParams,
    EpisodesSearchParams,
};
